import React from 'react';
import { AlertTriangle, CheckCircle, CreditCard, X } from 'lucide-react';
import { SubscriptionDetails } from '../lib/subscription';
import { useTranslation } from '../lib/LanguageContext';
import { formatRelativeDays } from '../lib/relativeDays';

interface SubscriptionBannerProps {
  subscription: SubscriptionDetails;
  polarCustomer: {
    polar_customer_id: string;
    subscription_id: string;
    subscription_status: string;
    cancel_at_period_end: boolean;
  } | null;
  onUpgrade: () => void;
  onManageSubscription: () => void;
  onDismiss: () => void;
}

export function SubscriptionBanner({ subscription, polarCustomer, onUpgrade, onManageSubscription, onDismiss }: SubscriptionBannerProps) {
  const { language, t } = useTranslation();
  const { subscriptionTier, taskLimit, currentTaskCount, percentageUsed, isWithinLimit, daysRemaining } = subscription;
  
  // Don't show banner if user is premium and:
  // - Not close to expiration (> 7 days), OR
  // - Subscription will auto-renew (cancel_at_period_end = false)
  if (subscriptionTier === 'premium' && (
    daysRemaining === null ||
    daysRemaining > 7 ||
    (polarCustomer?.subscription_status === 'active' && !polarCustomer?.cancel_at_period_end)
  )) {
    return null;
  }
  
  // For free plan, only show warning when approaching limit (80% or more)
  if (subscriptionTier === 'free' && percentageUsed < 80 && isWithinLimit) {
    return null;
  }
  
  // Premium expiring soon
  const isPremiumExpiringSoon = subscriptionTier === 'premium' && daysRemaining !== null && daysRemaining <= 7;
  
  let bannerColor = 'bg-indigo-50 border-indigo-200';
  let textColor = 'text-indigo-800';
  let progressColor = 'bg-indigo-500';
  
  if (!isWithinLimit || (subscriptionTier === 'free' && percentageUsed >= 80) || isPremiumExpiringSoon) {
    bannerColor = 'bg-amber-50 border-amber-200';
    textColor = 'text-amber-800';
    progressColor = 'bg-amber-500';
  }
  
  if (!isWithinLimit) {
    bannerColor = 'bg-red-50 border-red-200';
    textColor = 'text-red-800';
    progressColor = 'bg-red-500';
  }
  
  return (
    <div className={`relative border ${bannerColor} rounded-lg p-4 mt-4 mb-4`}>
      <button 
        onClick={onDismiss}
        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600"
      >
        <X className="h-5 w-5" />
      </button>
      
      <div className="flex items-start space-x-3">
        {!isWithinLimit ? (
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        ) : (subscriptionTier === 'free' && percentageUsed >= 80) || isPremiumExpiringSoon ? (
          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
        ) : (
          <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
        )}
        
        <div className="flex-1">
          <div className={`font-medium ${textColor}`}>
            {!isWithinLimit ? (
              t('bannerLimitReached')
            ) : isPremiumExpiringSoon ? (
              t('bannerPremiumExpires', { when: formatRelativeDays(daysRemaining, language) })
            ) : (subscriptionTier === 'free' && percentageUsed >= 80) ? (
              t('bannerApproachingLimit')
            ) : (
              subscriptionTier === 'free' ? t('bannerFreePlan') : t('bannerPremiumPlan')
            )}
          </div>
          
          <div className="mt-1 text-sm text-gray-600">
            {subscriptionTier === 'free'
              ? t('freeAllows', { limit: taskLimit })
              : t('premiumAllows', { limit: taskLimit })}
          </div>
          
          <div className="mt-2">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>{t('bannerNotesUsed', { current: currentTaskCount, limit: taskLimit })}</span>
              <span>{percentageUsed}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className={`${progressColor} h-2 rounded-full`} 
                style={{ width: `${percentageUsed}%` }}
              ></div>
            </div>
          </div>
          
          {(subscriptionTier === 'free' || isPremiumExpiringSoon) && (
            <button
              onClick={isPremiumExpiringSoon && polarCustomer?.polar_customer_id ? onManageSubscription : onUpgrade}
              className="mt-3 inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <CreditCard className="h-4 w-4 mr-1" />
              {subscriptionTier === 'free' ? t('upgradeToPremium') : polarCustomer?.polar_customer_id ? t('manageSubscription') : t('renewPremium')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}